import React, { useEffect, useMemo } from "react";
import Dropzone from "react-dropzone";
import Alert from "react-s-alert";

import EntityDropdown from "../../component/EntityDropdown";
import Loading from "../../component/Loading";
import { TAKS_IMPORT } from "../../config/lang";

import { call, upload } from "../../util/action";
import CreateMsgPopup from "../../util/CreateMsgPopup";
import { getStore } from "../../util/redux/store";
import { translate } from "../../util/util";

const me = getStore().getState().me;
function AddGroup({ backToGroupList, type, group, updateCheckboxIds, filterUrlKeys, onAllPointsAdded, clearPointsTrigger }) {
  const [activeGroup, setActiveGroup] = React.useState({});
  const [loading, setLoading] = React.useState({ status: false });
  const [status, setStatus] = React.useState({ type: "" });
  const [validate, setValidate] = React.useState({ status: false, type: "", message: "" });
  const [query, setQuery] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  useEffect(() => {
    if (group && group.id && type === "edit") {
      setActiveGroup(prevGroup => {
        return {
          ...group,
          name: prevGroup && prevGroup.name ? prevGroup.name : group.name,
          available_for_supervisors: prevGroup && prevGroup.available_for_supervisors ? prevGroup.available_for_supervisors : group.available_for_supervisors,
        };
      });
    } else if (group && group.points && group.points.length > 0) {
      setActiveGroup(prev => ({ ...prev, ...group }));
    } else if (group && group.points?.length === 0) {
      setActiveGroup(prev => ({ ...prev, points: group.points }));
    } else {
      setActiveGroup({});
    }

    setStatus(prev => ({
      ...prev,
      type,
    }));
  }, [group, type]);

  useEffect(() => {
    if (activeGroup.points) updateCheckboxIds("edit", activeGroup.points);
  }, [activeGroup.points]);

  useEffect(() => {
    if (clearPointsTrigger > 0) {
      setActiveGroup(prev => ({ ...prev, points: [] }));
    }
  }, [clearPointsTrigger]);

  const filteredData = useMemo(() => {
    const lowerCaseQuery = query.toLowerCase().trim();

    if (lowerCaseQuery.length < 3) {
      return activeGroup.points;
    }

    return activeGroup.points?.filter(item => item.name.toLowerCase().includes(lowerCaseQuery));
  }, [query, activeGroup.points]);

  const fetchGroupData = (data, type) => {
    if (!data && !type) {
      throw new Error("data or type is required");
    }
    const callObject =
      type === "edit"
        ? {
            method: "PUT",
            url: "point_groups/" + data?.id + "/",
            post: data,
          }
        : type === "delete"
        ? { method: "DELETE", url: "point_groups/" + data?.id + "/" }
        : type === "new"
        ? { method: "POST", url: "point_groups/", post: data }
        : null;
    call(callObject, response => {
      const isSuccess = !!response?.id;

      if (!isSuccess) {
        setSubmitting(false);
        setValidate({
          status: true,
          type: "error",
          message: translate(TAKS_IMPORT, "new_group_popup_error_update_text", me.language),
        });
        throw new Error("Response is not 204", response);
      } else {
        setValidate({
          status: false,
          type: "success",
          message: translate(TAKS_IMPORT, "new_group_popup_success_save_text", me.language),
        });
      }

      setTimeout(
        () => {
          backToGroupList();
        },
        isSuccess ? 1500 : 500
      );
    });
  };

  const handleInputNameChange = e => {
    setActiveGroup(prevGroup => ({
      ...prevGroup,
      name: e.target.value,
    }));
    setValidate({
      status: false,
      type: "",
      message: "",
    });
  };

  const onChangeUsersListChange = (name, item) => {
    const supervisorsIdArray = item.map(s => ({ id: s.id, name: s?.first_name + " " + s?.last_name }));
    setActiveGroup(prevGroup => ({
      ...prevGroup,
      available_for_supervisors: supervisorsIdArray,
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (submitting) return;
    let updatedData = {};

    if (!activeGroup.name?.trim()) {
      setValidate({
        status: false,
        type: "error",
        message: "new_group_popup_error_text",
      });
      return;
    }

    if (activeGroup.name?.trim().length < 5) {
      setValidate({
        status: false,
        type: "error",
        message: "new_group_popup_error_text_minimum",
      });
      return;
    }
    const supervisorsIdArray = activeGroup.available_for_supervisors?.map(s => s.id);
    const pointsIdArray = activeGroup.points?.map(row => row.id);

    if (status.type === "edit") {
      updatedData = {
        id: activeGroup.id || "",
        name: activeGroup.name || "",
        available_for_supervisors: supervisorsIdArray || [],
        points: pointsIdArray || [],
      };
    } else if (status.type === "new") {
      updatedData = {
        name: activeGroup.name || "",
        available_for_supervisors: supervisorsIdArray || [],
        points: pointsIdArray || [],
      };
    }
    setSubmitting(true);
    fetchGroupData(updatedData, status.type);
  };

  const getAllPoints = () => {
    setLoading({ status: true });
    const params = new URLSearchParams(filterUrlKeys).toString();
    const dynamicUrl = `points/all-points/${params ? `?${params}` : ""}`;
    call({ method: "GET", url: dynamicUrl }, response => {
      setActiveGroup(prevGroup => ({
        ...prevGroup,
        points: response.points,
      }));

      if (onAllPointsAdded) onAllPointsAdded();
      setLoading({ status: false });
    });
  };

  const removeItemFromGroup = pointId => {
    setActiveGroup(prevGroup => ({
      ...prevGroup,
      points: prevGroup.points.filter(point => point.id !== pointId),
    }));
  };

  const uploadFile = acceptedFiles => {
    let file = acceptedFiles[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setLoading({ status: true });

    upload({ method: "POST", url: "point_groups/points-ids-from-file/", post: formData }, response => {
      if (!!response && response.errors.length > 0) {
        const errortext = translate(TAKS_IMPORT, "file_error_uploaded", me.language) + " ( " + response?.errors[0]?.reason + " )";
        Alert.error(errortext, { position: "top" });
      }

      if (response && response.valid_ids && (response.errors.length === 0 || response.failed_count === 0)) {
        Alert.success(translate(TAKS_IMPORT, "file_successfully_uploaded", me.language), { position: "top" });
        setActiveGroup(prevGroup => ({
          ...prevGroup,
          points: response.valid_ids,
        }));
      }
      setLoading({ status: false });
    });
    setTimeout(() => {
      setLoading({ status: false });
    }, 5000);
  };

  return (
    <div className="ag-wrapper">
      {validate.status && <CreateMsgPopup setIsMsg={setValidate} isMsg={validate} />}
      <form onSubmit={handleSubmit}>
        <div className={`ag-field ${validate.type === "error" ? "ag-field--error" : ""}`}>
          <label className="ag-label" htmlFor="group-name">
            {translate(TAKS_IMPORT, "new_group_popup_field_name", me.language)}*
          </label>
          <input
            className="ag-input"
            value={activeGroup.name || ""}
            onChange={e => handleInputNameChange(e)}
            id="group-name"
            type="text"
            placeholder={translate(TAKS_IMPORT, "new_group_popup_field_name_placeholder", me.language)}
          />
          {validate.type === "error" && <p className="ag-error-msg">{translate(TAKS_IMPORT, validate.message, me.language)}</p>}
        </div>

        <div className="ag-field">
          <div className="ag-points-header">
            <label className="ag-label">{translate(TAKS_IMPORT, "new_group_popup_field_group_textarea_name", me.language)}</label>
            <input
              className="ag-filter-input"
              value={query || ""}
              onChange={e => setQuery(e.target.value)}
              type="text"
              placeholder={translate(TAKS_IMPORT, "group_popup_filter_name_placeholder", me.language)}
            />
          </div>
          <div className="ag-points-list dddd">
            {loading.status ? <Loading loading={loading.status} /> : null}
            {!filteredData?.length > 0 && activeGroup.points?.length > 0 && (
              <p className="ag-placeholder">{translate(TAKS_IMPORT, "group_popup_filter_name_empty_placeholder", me.language)}</p>
            )}
            {filteredData?.map(point => (
              <div className="ag-point-chip" key={point.id}>
                <span className="ag-point-chip-label">{point.name}</span>
                <button type="button" className="ag-point-chip-remove" onClick={() => removeItemFromGroup(point.id)}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            ))}
            {!activeGroup.points?.length > 0 && (
              <div className="ag-dropzone-block">
                <Dropzone
                  onDrop={acceptedFiles => uploadFile(acceptedFiles)}
                  accept="text/csv, .csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xlsx"
                  multiple={false}>
                  {({ getRootProps, getInputProps }) => (
                    <section className="dropzone">
                      <div {...getRootProps()} className="dropzone-field">
                        <input {...getInputProps()} />
                        <p>{translate(TAKS_IMPORT, "import_group_dragdrop_label", me.language)}</p>
                        <p className="approve-file">{translate(TAKS_IMPORT, "import_group_dragdrop_approve_file_text", me.language)}</p>
                      </div>
                    </section>
                  )}
                </Dropzone>
              </div>
            )}
          </div>
          <div className="ag-points-footer">
            <span className="ag-counter">
              {translate(TAKS_IMPORT, "added_points_text_label_suf", me.language) + " "}
              {filteredData?.length > 0 ? filteredData.length : 0}
              {" " + translate(TAKS_IMPORT, "added_points_text_label_pref", me.language)}
            </span>
            <button type="button" className="ag-add-all-btn" onClick={getAllPoints}>
              <span className="material-symbols-outlined">playlist_add</span>
              {translate(TAKS_IMPORT, "new_group_popup_field_group_add_all_btn_text", me.language)}
            </button>
          </div>
        </div>

        <div className="ag-field ag-field--users">
          <label className="ag-label">{translate(TAKS_IMPORT, "new_group_popup_field_group_available_users", me.language)}</label>
          <EntityDropdown
            entity="created_by"
            value={activeGroup.available_for_supervisors}
            onChange={o => {
              onChangeUsersListChange("users", o);
            }}
            placeholder={translate(TAKS_IMPORT, "new_group_popup_field_users_placeholder", me.language)}
            className="react-dropdown-container--multiselect"
            isMulti
          />
        </div>

        <div className="ag-actions">
          <button type="submit" className="ag-save-btn" disabled={validate.type === "error" || submitting}>
            <span className="material-symbols-outlined">save</span>
            {translate(TAKS_IMPORT, "new_group_popup_btn_save_name", me.language)}
          </button>
          {validate.type === "success" && (
            <span className="ag-success-msg">
              <span className="material-symbols-outlined">check_circle</span>
              {validate.message}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddGroup;
